const DEFAULTS = {
	color: "#d8d8d8",
	lineCount: 54,
	amplitude: 46,
	speed: 0.0012,
	opacity: 0.28,
	mouseInteraction: false,
};

const getCssNumber = (element, name, fallback) => {
	const value = getComputedStyle(element).getPropertyValue(name).trim();
	const number = Number.parseFloat(value);
	return Number.isFinite(number) ? number : fallback;
};

const getCssValue = (element, name, fallback) => {
	const value = getComputedStyle(element).getPropertyValue(name).trim();
	return value || fallback;
};

const createThreadBackground = (container) => {
	if (container.dataset.threadsBgReady === "true") return;
	container.dataset.threadsBgReady = "true";

	const canvas = document.createElement("canvas");
	canvas.className = "threads-bg__canvas";
	canvas.setAttribute("aria-hidden", "true");
	container.prepend(canvas);

	const context = canvas.getContext("2d", { alpha: true });
	if (!context) return;

	const state = {
		width: 0,
		height: 0,
		dpr: Math.min(window.devicePixelRatio || 1, 2),
		frame: null,
		mouse: { x: 0, y: 0, active: false },
		reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
	};

	const options = {
		color: getCssValue(container, "--threads-color", DEFAULTS.color),
		lineCount: getCssNumber(container, "--threads-count", DEFAULTS.lineCount),
		amplitude: getCssNumber(container, "--threads-amplitude", DEFAULTS.amplitude),
		speed: getCssNumber(container, "--threads-speed", DEFAULTS.speed),
		opacity: getCssNumber(container, "--threads-opacity", DEFAULTS.opacity),
		mouseInteraction: container.dataset.threadsMouse === "true" || DEFAULTS.mouseInteraction,
	};

	const resize = () => {
		const rect = container.getBoundingClientRect();
		state.width = Math.max(1, Math.round(rect.width));
		state.height = Math.max(1, Math.round(rect.height));
		state.dpr = Math.min(window.devicePixelRatio || 1, 2);

		canvas.width = Math.round(state.width * state.dpr);
		canvas.height = Math.round(state.height * state.dpr);
		canvas.style.width = `${state.width}px`;
		canvas.style.height = `${state.height}px`;
		context.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
	};

	const drawThread = (index, time) => {
		const { width, height, mouse } = state;
		const progress = options.lineCount <= 1 ? 0 : index / (options.lineCount - 1);
		const yBase = height * (0.18 + progress * 0.68);
		const phase = time * options.speed + progress * Math.PI * 3.8;
		const drift = Math.sin(phase * 0.6) * options.amplitude * 0.35;
		const wave = Math.sin(phase + progress * 2.4) * options.amplitude;
		const spread = options.amplitude * (0.7 + Math.sin(progress * Math.PI) * 0.65);
		const mousePull = options.mouseInteraction && mouse.active ? (mouse.y - yBase) * 0.055 : 0;
		const mouseX = options.mouseInteraction && mouse.active ? mouse.x : width * 0.5;

		const startX = -width * 0.08;
		const endX = width * 1.08;
		const c1x = width * (0.18 + Math.sin(phase) * 0.035);
		const c2x = width * (0.74 + Math.cos(phase * 0.8) * 0.04);
		const c1y = yBase - spread + drift;
		const c2y = yBase + wave + mousePull;
		const endY = height * (0.2 + (1 - progress) * 0.62) + Math.cos(phase) * spread * 0.55;

		context.beginPath();
		context.moveTo(startX, yBase + Math.sin(phase) * 12);
		context.bezierCurveTo(c1x, c1y, mouseX * 0.38 + c2x * 0.62, c2y, endX, endY);
		context.stroke();
	};

	const render = (time = 0) => {
		context.clearRect(0, 0, state.width, state.height);
		context.globalCompositeOperation = "source-over";
		context.lineWidth = 1;
		context.strokeStyle = options.color;
		context.globalAlpha = options.opacity;

		context.save();
		context.translate(state.width, state.height);
		context.rotate(Math.PI);

		for (let index = 0; index < options.lineCount; index += 1) {
			drawThread(index, state.reducedMotion ? 0 : time);
		}

		context.restore();
		context.globalAlpha = 1;
	};

	const draw = (time = 0) => {
		render(time);

		if (!state.reducedMotion) {
			state.frame = window.requestAnimationFrame(draw);
		}
	};

	const onPointerMove = (event) => {
		if (!options.mouseInteraction) return;
		const rect = canvas.getBoundingClientRect();
		state.mouse.x = event.clientX - rect.left;
		state.mouse.y = event.clientY - rect.top;
		state.mouse.active = true;
	};

	const onPointerLeave = () => {
		state.mouse.active = false;
	};

	const observer = new ResizeObserver(() => {
		resize();
		render(state.reducedMotion ? 0 : performance.now());
	});

	observer.observe(container);
	resize();
	draw();

	container.addEventListener("pointermove", onPointerMove);
	container.addEventListener("pointerleave", onPointerLeave);
};

export const threadsBackground = () => {
	const containers = document.querySelectorAll(".threads-bg");
	if (!containers.length) return;

	containers.forEach(createThreadBackground);
};
